// api-client/utils.ts
/**
 * @file utils.ts
 * @description Utility functions for the ApiClient module.
 */
import type { AxiosResponse } from 'axios'; // Only for type, not a runtime dependency if only used here

/**
 * Creates a single AbortSignal that aborts when any of the provided signals abort,
 * or if any of the signals are already aborted.
 * Uses AbortSignal.any() if available, otherwise provides a basic fallback.
 * Filters out undefined/null signals before processing.
 *
 * @param signals An array of AbortSignal instances (can include undefined).
 * @returns A single AbortSignal, or undefined if no valid signals are provided.
 */
export function createCombinedSignal(
  signals: (AbortSignal | undefined)[],
): AbortSignal | undefined {
  const validSignals = signals.filter(
    (s): s is AbortSignal => s instanceof AbortSignal,
  );

  if (validSignals.length === 0) {
    return undefined;
  }
  if (validSignals.length === 1) {
    return validSignals[0];
  }

  if (typeof AbortSignal.any === 'function') {
    return AbortSignal.any(validSignals);
  }

  const controller = new AbortController();
  const onAbort = (event: Event) => {
    const originalSignal = event.target as AbortSignal;
    controller.abort(
      originalSignal?.reason ?? 'Signal aborted by one of the combined signals',
    );
  };

  for (const signal of validSignals) {
    if (signal.aborted) {
      controller.abort(signal.reason ?? 'Signal was already aborted');
      break;
    }
    signal.addEventListener('abort', onAbort, {
      once: true,
      signal: controller.signal,
    });
  }
  return controller.signal;
}

/**
 * Normalizes Axios headers to the format expected by RawApiResponse.
 * AxiosHeaderValue can be string, number, boolean, or null. We want string, string[], or undefined.
 */
export function normalizeAxiosHeaders(
  headers: AxiosResponse['headers'], // Using AxiosResponse['headers'] for accurate typing
): Record<string, string | string[] | undefined> {
  const normalized: Record<string, string | string[] | undefined> = {};
  if (!headers) {
    return normalized;
  }
  for (const key in headers) {
    // eslint-disable-next-line no-prototype-builtins
    if (Object.prototype.hasOwnProperty.call(headers, key)) {
      const value = headers[key];
      if (value === null) {
        normalized[key] = undefined;
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        normalized[key] = String(value);
      } else {
        normalized[key] = value; // string | string[] | undefined are fine
      }
    }
  }
  return normalized;
}
